import type { OutboxRecord, OutboxStore } from './outbox'
import { scopeOf } from './outbox'
import type { FieldClockSnapshot, FieldAttendanceSnapshot } from '@/lib/fieldClock'

export interface CrewJob { id: string; address: string; scopeOfWork: string | null; updatedAt: string; scheduledStart: string | null; scheduledEnd?: string | null; status?: 'SCHEDULED' | 'IN_PROGRESS' }
export interface CrewSnapshot { workspaceId: string; userId: string; day: string; timezone: string; savedAt: string; jobs: CrewJob[] }
export interface SavedFieldState { active: FieldClockSnapshot | null; attendance: FieldAttendanceSnapshot | null; savedAt: string }
export const OFFLINE_SCOPE_KEY = 'cb.crew-offline.active.v1'
const ACTIVE = OFFLINE_SCOPE_KEY
export const activeScope = () => localStorage.getItem(ACTIVE)
export function lockOfflineCrew() { localStorage.removeItem(ACTIVE); window.dispatchEvent(new Event('crew-offline-scope')) }
export function activateOfflineCrew(scope: string) { localStorage.setItem(ACTIVE, scope); window.dispatchEvent(new Event('crew-offline-scope')) }
export function checkOfflineIdentity(identity: { id: string; workspaceId?: string } | null) {
  if (!identity?.workspaceId || activeScope() !== scopeOf({ workspaceId: identity.workspaceId, userId: identity.id })) lockOfflineCrew()
}

async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Released note-only clients open version 1 on every read/write. Upgrading
    // in place preserves their notes while fencing old tabs before field data
    // exists, including their late ACK/error writes after device erasure.
    const req = indexedDB.open('cb-crew-offline-v1', 2)
    let abandoned = false
    req.onupgradeneeded = () => {
      if (abandoned) { req.transaction?.abort(); return }
      if (!req.result.objectStoreNames.contains('snapshots')) req.result.createObjectStore('snapshots')
      if (!req.result.objectStoreNames.contains('outbox')) {
        const outbox = req.result.createObjectStore('outbox', { keyPath: ['scope', 'operation.id'] })
        outbox.createIndex('scope', 'scope')
      }
    }
    req.onsuccess = () => {
      if (abandoned) { req.result.close(); return }
      req.result.onversionchange = () => req.result.close()
      resolve(req.result)
    }
    req.onerror = () => reject(req.error ?? new Error('Device storage unavailable'))
    req.onblocked = () => {
      abandoned = true
      const error = new Error('Close other tabs and reload to update device storage')
      error.name = 'OfflineStorageUpgradeBlockedError'
      reject(error)
    }
  })
}
async function run<T>(name: string, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await database()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, mode)
    const req = action(tx.objectStore(name))
    // A successful request can still be rolled back (quota/disk failure).
    // Tell the user it is saved only after the transaction commits.
    tx.oncomplete = () => { db.close(); resolve(req.result) }
    tx.onabort = tx.onerror = () => { db.close(); reject(tx.error ?? new Error('Device storage unavailable')) }
  })
}
export const indexedOutbox: OutboxStore = {
  list: async scope => (await run<OutboxRecord[]>('outbox', 'readonly', store => store.index('scope').getAll(scope))).sort((a, b) => a.operation.deviceAt.localeCompare(b.operation.deviceAt) || a.operation.id.localeCompare(b.operation.id)),
  get: async (scope, id) => run<OutboxRecord | undefined>('outbox', 'readonly', store => store.get([scope, id])),
  add: async record => { await run('outbox', 'readwrite', store => store.add(record)) },
  put: async record => {
    const db = await database()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('outbox', 'readwrite')
      const store = tx.objectStore('outbox')
      const prior = store.get([record.scope, record.operation.id])
      prior.onsuccess = () => {
        // Another tab may finish deleting while a sync request is in flight.
        // Its device-copy tombstone must win over that older ACK/error write.
        if (prior.result?.state !== 'removed' || record.state === 'removed') store.put(record)
      }
      tx.oncomplete = () => { db.close(); resolve() }
      tx.onabort = tx.onerror = () => { db.close(); reject(tx.error ?? new Error('Device storage unavailable')) }
    })
  },
}
export const saveSnapshot = async (snapshot: CrewSnapshot) => { await run('snapshots', 'readwrite', store => store.put(snapshot, scopeOf(snapshot))) }
export async function readSnapshot(scope: string) {
  if (activeScope() !== scope) return undefined
  const snapshot = await run<CrewSnapshot | undefined>('snapshots', 'readonly', store => store.get(scope))
  return activeScope() === scope ? snapshot : undefined
}
const fieldKey = (scope: string, jobId: string) => JSON.stringify(['field-state', scope, jobId])
export async function saveFieldState(scope: string, jobId: string, state: SavedFieldState) {
  if (activeScope() !== scope) throw new Error('Account changed')
  await run('snapshots', 'readwrite', store => store.put(state, fieldKey(scope, jobId)))
}
export async function readFieldState(scope: string, jobId: string) {
  if (activeScope() !== scope) return undefined
  const state = await run<SavedFieldState | undefined>('snapshots', 'readonly', store => store.get(fieldKey(scope, jobId)))
  return activeScope() === scope ? state : undefined
}
